import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { SettingsCard } from '../../components/composed';
import { AddressCard, AddressForm } from '../../components/domain/address';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AddressSettings = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/addresses/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAddresses(data);
    } catch (err) {
      console.error('Error fetching addresses:', err);
      setError("You don't have any saved locations yet. Add a location to get started.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSuccess = (savedAddress) => {
    if (editingAddress) {
      // Update existing address in the list
      setAddresses(addresses.map(addr => 
        addr.id === savedAddress.id ? savedAddress : addr
      ));
      setEditingAddress(null);
    } else {
      // Add new address to the list
      setAddresses([...addresses, savedAddress]);
    }
    // Close modal
    setOpen(false);
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setOpen(true);
  };

  const handleCreate = () => {
    setEditingAddress(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingAddress(null);
  };

  const createButton = (
    <IconButton
      size="small"
      onClick={handleCreate}
      sx={{
        color: '#666666',
      }}
      title="Create Address"
    >
      <Add fontSize="small" />
    </IconButton>
  );

  if (loading) {
    return (
      <>
        <SettingsCard
          title="Saved Locations"
          sx={{ mb: 0 }}
          endActions={createButton}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </SettingsCard>
        <AddressForm
          open={open}
          onClose={handleClose}
          onSave={handleSaveSuccess}
          editAddress={editingAddress}
        />
      </>
    );
  }

  if (error) {
    return (
      <>
        <SettingsCard
          title="Saved Locations"
          sx={{ mb: 0 }}
          endActions={createButton}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        </SettingsCard>
        <AddressForm
          open={open}
          onClose={handleClose}
          onSave={handleSaveSuccess}
          editAddress={editingAddress}
        />
      </>
    );
  }

  return (
    <>
      <SettingsCard
        title="Saved Locations"
        sx={{ mb: 0 }}
        endActions={createButton}
      >
        {addresses.length === 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                elevation={1}
                sx={{
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#ffffff',
                  minHeight: 200,
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" align="center">
                    No saved locations.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {addresses.map((address) => (
              <Grid item xs={12} md={4} key={address.id}>
                <AddressCard
                  address={address}
                  onEdit={handleEdit}
                  showDefaultRadius={true}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </SettingsCard>

      <AddressForm
        open={open}
        onClose={handleClose}
        onSave={handleSaveSuccess}
        editAddress={editingAddress}
      />
    </>
  );
};

export default AddressSettings;
